<template>
  <v-row id="row" >
    <canvas ref="tp-cnv" style="left: 0" height="100"></canvas>
    <exo-form-alert v-if="error.length" type="warning">
      {{ this.error }}
    </exo-form-alert>
  </v-row>
</template>

<script>
export default {
  name: 'exo-thrust-pattern-canvas',

  props: {
    tp_thrust: {
      type: String | Number,
      default: '1'
    },
    tp_rest: {
      type: String | Number,
      default: '0'
    },
    tp_shift: {
      type: String | Number,
      default: '0'
    },
    tp_arc: {
      type: String,
      default: 'TWO'
    },
    tp_duty_cycle: {
      type: String | Number,
      default: '30'
    }
  },
  data() {
    return {
      ARC_UP_THRUST_PATTERN: [],
      ARC_DOWN_THRUST_PATTERN: [],
      PI: Math.PI,
      TWO_PI: Math.PI * 2,
      ROW_SIZE:750,
      MAX_ORBIT_PER_ROW:15,
      error: ''
    }
  },
  mounted() {
    this.updateImage()
  },
  computed: {
    patternWatcher() {
      return (
        this.tp_thrust +
        '-' +
        this.tp_rest +
        '-' +
        this.tp_shift +
        '-' +
        this.tp_arc +
        '-' +
        this.tp_duty_cycle+
        '-' +
        this.ROW_SIZE
      )
    },
    nbArcValue() {
      if (this.tp_arc === 'ONE') {
        return 1
      }
      return 2
    }
  },
  watch: {
    patternWatcher: function (value) {
      this.error = ''
      this.updateImage()
    }
  },
  methods: {
    buildFromInputs() {
      // Reset thrust pattern
      this.ARC_UP_THRUST_PATTERN = []
      this.ARC_DOWN_THRUST_PATTERN = []

      // Arc up thrust
      for (let i = 0; i < parseInt(this.tp_thrust); i++) {
        this.ARC_UP_THRUST_PATTERN.push(1)
      }

      // Arc up rest
      for (let i = 0; i < parseInt(this.tp_rest); i++) {
        this.ARC_UP_THRUST_PATTERN.push(0)
      }

      // Arc down
      if (this.nbArcValue > 1) {
        // Arc down no shift
        this.ARC_DOWN_THRUST_PATTERN = [...this.ARC_UP_THRUST_PATTERN]

        // Arc down shift
        if (parseInt(this.tp_shift) > 0) {
          for (let i = 0; i < parseInt(this.tp_shift); i++) {
            let lastElt = this.ARC_DOWN_THRUST_PATTERN.pop() // Remove & get the last element
            this.ARC_DOWN_THRUST_PATTERN.unshift(lastElt) // Push the previous last element at begin
          }
        }
      }
    },
    draw(el) {
      // init canva
      let ctx = this.initCanva(el)
      let dutyCycle = parseInt(this.tp_duty_cycle)
      dutyCycle = this.nbArcValue > 1 ? dutyCycle : dutyCycle * 2

      // Draw stroke circle shape
      for (let i = 0; i < this.ARC_UP_THRUST_PATTERN.length; i++) {
        if (i <  this.MAX_ORBIT_PER_ROW) {
          this.fullCircleStroke(ctx, i > 0 ? 25 + 50 * i : 25, 25, 20)
        } else {
          this.fullCircleStroke(ctx, i > this.MAX_ORBIT_PER_ROW ? 25 + 50 * (i - this.MAX_ORBIT_PER_ROW) : 25, 75, 20)
        }
      }

      // Draw arc up
      for (let i = 0; i < this.ARC_UP_THRUST_PATTERN.length; i++) {
        // Rest orbit
        if (this.ARC_UP_THRUST_PATTERN[i] === 0) {
          // Rest orbit so empty content fill
        }

        // Thrust orbit
        if (this.ARC_UP_THRUST_PATTERN[i] === 1) {
          if (i < this.MAX_ORBIT_PER_ROW) {
          this.halfUpCircleFill(
            ctx,
            i > 0 ? 25 + 50 * i : 25,
            25,
            20,
            dutyCycle
          )
          } else {
            this.halfUpCircleFill(
              ctx,
              i > this.MAX_ORBIT_PER_ROW ? 25 + 50 * (i - this.MAX_ORBIT_PER_ROW) : 25,
              75,
              20,
              dutyCycle
            )
          }
        }
      }

      // Draw arc down
      if (
        this.ARC_DOWN_THRUST_PATTERN.length !== null &&
        this.ARC_DOWN_THRUST_PATTERN.length > 0
      ) {
        for (let i = 0; i < this.ARC_DOWN_THRUST_PATTERN.length; i++) {
          // Rest orbit
          if (this.ARC_DOWN_THRUST_PATTERN[i] === 0) {
            // Rest orbit so empty content fill
          }

          // Thrust orbit
          if (this.ARC_DOWN_THRUST_PATTERN[i] === 1) {
            if (i < this.MAX_ORBIT_PER_ROW) {
              this.halfDownCircleFill(
                ctx,
                i > 0 ? 25 + 50 * i : 25,
                25,
                20,
                dutyCycle
              )
            } else {
              this.halfDownCircleFill(
              ctx,
              i > this.MAX_ORBIT_PER_ROW ? 25 + 50 * (i - this.MAX_ORBIT_PER_ROW) : 25,
              75,
              20,
              dutyCycle
              )
            }
          }
        }
      }
    },
    fullCircleStroke(ctx, x, y, r) {
      ctx.beginPath()
      ctx.arc(x, y, r, 0, this.TWO_PI, false)
      ctx.closePath()
      ctx.stroke()
    },
    halfUpCircleFill(ctx, x, y, r, percentOfPi) {
      let arcAngleOmega = this.PI - this.PI * (percentOfPi / 100)
      let arcAngleStart = this.PI + 0.5 * arcAngleOmega
      let arcAngleEnd = arcAngleStart + this.PI * (percentOfPi / 100)
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.arc(x, y, r, arcAngleStart, arcAngleEnd, false)
      ctx.closePath()
      ctx.fill()
    },
    halfDownCircleFill(ctx, x, y, r, percentOfPi) {
      let arcAngleOmega = this.PI - this.PI * (percentOfPi / 100)
      let arcAngleStart = 0.5 * arcAngleOmega
      let arcAngleEnd = arcAngleStart + this.PI * (percentOfPi / 100)
      ctx.beginPath()
      ctx.moveTo(x, y)
      ctx.arc(x, y, r, arcAngleStart, arcAngleEnd, false)
      ctx.closePath()
      ctx.fill()
    },
    initCanva(el) {
      const row = document.getElementById('row')
      window.addEventListener('resize', () => {
        this.ROW_SIZE = row.offsetWidth != 0 ? row.offsetWidth : this.ROW_SIZE
        this.MAX_ORBIT_PER_ROW = parseInt(this.ROW_SIZE/50)
      })
      // Configure canva
      el.setAttribute('width', this.ARC_UP_THRUST_PATTERN.length * 55)
      let ctx = el.getContext('2d')
      // Reset canva
      ctx.clearRect(0, 0, el.width, el.height)
      return ctx
    },
    updateImage() {
      if (parseInt(this.tp_thrust, 10) + parseInt(this.tp_rest, 10) <= 2 * this.MAX_ORBIT_PER_ROW) {
        this.buildFromInputs()
        this.draw(this.$refs['tp-cnv'])
      } else {
        let el = this.$refs['tp-cnv']
        let ctx = el.getContext('2d')
        ctx.clearRect(0, 0, el.width, el.height)
        this.error = 'Thrust pattern is too long to display'
      }
    }
  }
}
</script>
